import { useQuery } from "react-query";

import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

import { GeographyArraySchema, geographySearchApiKey, looksLikeGeographyCode, type Geography } from "./geographySchema";

type UseGeographySearchOptions = {
    searchText?: string;
    type?: Geography["type"];
    enabled?: boolean;
    itemsPerPage?: number;
};

export const useGeographySearch = ({
    searchText = "",
    type,
    enabled = true,
    itemsPerPage = 20,
}: UseGeographySearchOptions = {}) => {
    const trimmedSearch = searchText.trim();
    const isCodeQuery = looksLikeGeographyCode(trimmedSearch);
    const canSearch = trimmedSearch.length >= (isCodeQuery ? 1 : 2);

    const query = useQuery({
        queryKey: [geographySearchApiKey, trimmedSearch, type ?? "all", itemsPerPage],
        queryFn: async () => {
            const params: Record<string, string | number | Record<string, string>> = {
                itemsPerPage,
                order: { code: "ASC" },
            };

            if (isCodeQuery) {
                params.code = trimmedSearch.toUpperCase();
            } else {
                params.name = trimmedSearch;
            }

            if (type !== undefined) {
                params.type = type;
            }

            const response = await loggedInApiCall.get<{ "hydra:member": unknown; "hydra:totalItems": number }>(
                "/api/geographies",
                { params },
            );

            return response.data;
        },
        select: (data) => {
            if (!data?.["hydra:member"]) {
                return [];
            }

            return zodParseWithLog(data["hydra:member"], GeographyArraySchema, geographySearchApiKey);
        },
        enabled: enabled && canSearch,
        staleTime: 30_000,
        cacheTime: 300_000,
    });

    return {
        geographies: query.data ?? [],
        isCodeQuery,
        ...query,
    };
};
